/**
 * One activity (M1 track D4).
 *
 * The screen the last two stages of the golden path hang off: D5 adds the
 * assignment panel here, D6 adds attendance. Both belong on this page because
 * "who was asked to come" and "who came" are the same conversation, and staff
 * record the second while looking at the first.
 */

import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  ACTIVITY_STATUS,
  ACTIVITY_STATUS_TRANSITIONS,
  formatDuration,
} from '@parinaam-vms/shared';
import type { ActivityWithProgram } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DeletePanel,
  DetailList,
  DurationInput,
  ErrorState,
  LoadingState,
  PageHeader,
  SelectInput,
  StatusBadge,
  TextArea,
  TextInput,
} from '../../components/common';
import { useActivity, useDeleteActivity, useUpdateActivity } from '../../hooks/usePrograms';
import { useActivitySessions, useSetSessionCapacity } from '../../hooks/useSessions';
import type { Session } from '../../services/api/sessions';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/common/Toast';
import { SessionPanel } from './SessionPanel';
import { AddSessionPanel } from './AddSessionPanel';
import { AssignmentPanel } from './AssignmentPanel';
import { AttendancePanel } from './AttendancePanel';
import type { ActivityBody } from '../../services/api/programs';
import { CRUMB, cameFrom } from '../../constants/breadcrumbs';
import { formatDateRange, formatTimeRange, humaniseCode, orDash } from '../../utils/format';
import styles from './ActivitiesPage.module.css';

export function ActivityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [editing, setEditing] = useState(false);

  /*
    Which list opened this screen, so the way back retraces it.

    An activity has two homes — its program, and the flat Activities list —
    and the URL is the same from either. Before this the trail always named the
    program, so somebody who had come through Activities was offered a way
    back that skipped it and landed them in Programs (Parinaam, 13 Sep 2026).

    Undefined for a pasted link, a bookmark, or a link from a third screen. That
    is the normal case as much as the other two, and it falls back to the
    structural trail below.
  */
  const origin = cameFrom(useLocation().state);

  const query = useActivity(id);

  if (query.isPending) return <LoadingState label="Loading the activity…" />;
  if (query.isError) {
    return (
      <>
        {/* Breadcrumbs on the error branch too: a record that will not load is
            exactly when staff need the way back. */}
        {/* The error branch has no record to name a program from, so the flat
            list is the only trail it can honestly draw. */}
        <PageHeader title="Activity" breadcrumbs={[CRUMB.activities, { label: 'Activity' }]} />
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </>
    );
  }

  const activity = query.data as ActivityWithProgram;

  return (
    <>
      <PageHeader
        title={activity.name}
        description={`${activity.program_name} · ${formatDateRange(activity.start_date, activity.end_date)}`}
        /*
          Under its program, not under the flat Activities list: an activity
          cannot exist without a program (§7.13), and this is the only place the
          screen offers a route up to it. Activities stays one click away in the
          sidebar for anyone who wants the flat view.
        */
        breadcrumbs={
          origin === 'activities'
            ? [CRUMB.activities, { label: activity.name }]
            : [
                CRUMB.programs,
                { label: activity.program_name, to: `/programs/${activity.program_id}` },
                { label: activity.name },
              ]
        }
        actions={
          <div className={styles.headerActions}>
            <StatusBadge status={activity.status} />
            {!editing && activity.status !== ACTIVITY_STATUS.DELETED && (
              <Button variant="primary" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
          </div>
        }
      />

      {editing ? (
        <EditActivityForm activity={activity} onClose={() => setEditing(false)} />
      ) : (
        <Card title="Details">
          <DetailList
            items={[
              { label: 'Activity name', value: activity.name },
              { label: 'Activity short code', value: orDash(activity.activity_code) },
              {
                label: 'Activity date',
                value: formatDateRange(activity.start_date, activity.end_date),
              },
              {
                label: 'Activity time',
                value: formatTimeRange(activity.start_time, activity.end_time),
              },
              { label: 'Activity location', value: orDash(activity.location) },
              { label: 'Activity program', value: activity.program_name },
              {
                // The total of its sessions' hours, so it cannot disagree with them.
                label: 'Number of hours per activity',
                value:
                  activity.planned_minutes == null ? '—' : formatDuration(activity.planned_minutes),
              },
              {
                label: 'Usual time given',
                value: activity.default_duration_minutes
                  ? formatDuration(activity.default_duration_minutes)
                  : '—',
              },
              { label: 'Status', value: humaniseCode(activity.status) },
              { label: 'Activity description', value: orDash(activity.description), wide: true },
            ]}
          />
        </Card>
      )}

      {/* Above the roster: what the work is comes before who is doing it. */}
      <SessionPanel activityId={activity.activity_id} />

      {/* Adding a session is what creates a phase to assign against (D19). */}
      <AddSessionPanel activity={activity} />

      <AssignmentPanel
        activityId={activity.activity_id}
        activityClosed={
          activity.status === ACTIVITY_STATUS.CANCELLED ||
          activity.status === ACTIVITY_STATUS.DELETED
        }
        closedMessage={
          activity.status === ACTIVITY_STATUS.DELETED
            ? 'This activity has been deleted.'
            : 'This activity has been cancelled.'
        }
      />

      {/* Below the roster, because attendance is recorded against that list. */}
      <AttendancePanel activity={activity} />

      <DeleteActivity activity={activity} />
    </>
  );
}

/**
 * Deleting the activity — admin only, and last on the page.
 *
 * Hiding it from a TEAM account is presentation, not security: the API refuses
 * them outright. It is here because the people who run activities day to day
 * should not have the one irreversible control in their eyeline.
 */
function DeleteActivity({ activity }: { activity: ActivityWithProgram }) {
  const { hasRole } = useAuth();
  const mutation = useDeleteActivity(activity.activity_id);

  if (!hasRole('ADMIN')) return null;

  return (
    <DeletePanel
      title="Delete this activity"
      noun="activity"
      deleted={activity.status === ACTIVITY_STATUS.DELETED}
      pending={mutation.isPending}
      error={mutation.isError ? (mutation.error as Error) : null}
      onDelete={(reason) => mutation.mutate(reason)}
      consequences={
        <>
          <p>
            <strong>This cannot be undone.</strong> {activity.name} will be deleted permanently. It
            cannot be edited or reinstated afterwards, and no new roster places or attendance can be
            recorded against it.
          </p>
          <p>
            Any hours already recorded against it stay in the record but{' '}
            <strong>stop counting towards every figure on the dashboard</strong> — including total
            volunteer hours. If the activity simply did not go ahead, cancel it instead: that keeps
            the hours and reverses.
          </p>
        </>
      }
    />
  );
}

function EditActivityForm({
  activity,
  onClose,
}: {
  activity: ActivityWithProgram;
  onClose: () => void;
}) {
  const mutation = useUpdateActivity(activity.activity_id);
  const capacity = useSetSessionCapacity();
  const sessions = useActivitySessions(activity.activity_id);
  const toast = useToast();

  /*
   * The limits are per SESSION, not per activity (migration 043): one activity
   * can carry several and each needs its own number. Held as strings because
   * that is what a number input gives back, and because '' has to stay
   * distinguishable from '0' - the first means "no cap", the second is refused.
   */
  const [limits, setLimits] = useState<Record<string, string>>({});
  const limitFor = (sessionId: string, stored: number | null) =>
    limits[sessionId] ?? (stored === null ? '' : String(stored));

  const [defaultMinutes, setDefaultMinutes] = useState(activity.default_duration_minutes ?? 0);

  const [form, setForm] = useState({
    name: activity.name,
    start_date: activity.start_date,
    end_date: activity.end_date ?? '',
    // A TIME comes back as HH:MM:SS; the input works in HH:MM.
    start_time: activity.start_time?.slice(0, 5) ?? '',
    end_time: activity.end_time?.slice(0, 5) ?? '',
    location: activity.location ?? '',
    activity_code: activity.activity_code ?? '',
    status: activity.status as string,
    description: activity.description ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (field: string) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: '' }));
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const problems: Record<string, string> = {};
    if (form.name.trim() === '') problems.name = 'An activity needs a name.';
    if (form.start_date === '') problems.start_date = 'When does this activity start?';
    if (form.end_date && form.end_date < form.start_date) {
      problems.end_date = 'The end date must be on or after the start date.';
    }
    if (form.start_time && form.end_time && form.end_time <= form.start_time) {
      problems.end_time = 'The end time must be after the start time.';
    }
    for (const session of sessions.data ?? []) {
      const entered = limits[session.session_id];
      if (entered !== undefined && entered !== '') {
        if (!/^\d+$/.test(entered) || Number(entered) < 1) {
          problems[`limit:${session.session_id}`] =
            'Enter a whole number of at least 1, or leave it blank for no limit.';
        }
      }
    }
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    const next: Record<string, string | null> = {
      name: form.name.trim(),
      start_date: form.start_date,
      end_date: form.end_date || null,
      start_time: form.start_time || null,
      end_time: form.end_time || null,
      location: form.location.trim() || null,
      activity_code: form.activity_code.trim() || null,
      status: form.status,
      description: form.description.trim() || null,
    };

    const changes: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(next)) {
      let stored = (activity as unknown as Record<string, unknown>)[key] ?? null;
      if ((key === 'start_time' || key === 'end_time') && typeof stored === 'string') {
        stored = stored.slice(0, 5);
      }
      if (value !== stored) changes[key] = value;
    }
    if (defaultMinutes !== (activity.default_duration_minutes ?? 0)) {
      // Zero means "no default" on the way out, the same as never having set
      // one, because the column is nullable and the sweep skips a null.
      changes.default_duration_minutes = defaultMinutes > 0 ? defaultMinutes : null;
    }

    // The limits are separate writes to separate sessions, so they are collected
    // rather than folded into the activity PATCH.
    const capacityChanges = (sessions.data ?? [])
      .map((session: Session) => {
        const entered = limits[session.session_id];
        if (entered === undefined) return null;
        const wanted = entered === '' ? null : Number(entered);
        if (wanted === session.volunteers_needed) return null;
        return { sessionId: session.session_id, volunteersNeeded: wanted };
      })
      .filter(
        (change: { sessionId: string; volunteersNeeded: number | null } | null): change is {
          sessionId: string;
          volunteersNeeded: number | null;
        } => Boolean(change)
      );

    if (Object.keys(changes).length === 0 && capacityChanges.length === 0) {
      toast.info('No changes to save.');
      onClose();
      return;
    }

    /*
     * The activity first, then each session whose limit moved, then close once.
     *
     * Sequential rather than fired together: a 409 on one limit must not leave
     * the form closed with the others half applied, and the error that surfaces
     * should be the first thing that actually failed.
     */
    void (async () => {
      try {
        if (Object.keys(changes).length > 0) {
          await mutation.mutateAsync(changes as ActivityBody);
        }
        for (const change of capacityChanges) {
          await capacity.mutateAsync(change);
        }
        onClose();
      } catch (error) {
        toast.error(error, 'Could not save the activity.');
      }
    })();
  };

  return (
    <Card title="Edit activity">
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className={styles.error} role="alert">
            {(mutation.error as Error).message}
          </p>
        )}

        <fieldset className={styles.section} disabled={mutation.isPending}>
          <div className={styles.row}>
            <TextInput
              label="Activity name"
              required
              value={form.name}
              error={errors.name}
              onChange={(event) => set('name')(event.target.value)}
            />
            <TextInput
              label="Activity short code"
              value={form.activity_code}
              onChange={(event) => set('activity_code')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Activity start date"
              type="date"
              required
              value={form.start_date}
              error={errors.start_date}
              onChange={(event) => set('start_date')(event.target.value)}
            />
            <TextInput
              label="Activity end date"
              type="date"
              value={form.end_date}
              error={errors.end_date}
              onChange={(event) => set('end_date')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <TextInput
              label="Activity start time"
              type="time"
              value={form.start_time}
              error={errors.start_time}
              onChange={(event) => set('start_time')(event.target.value)}
            />
            <TextInput
              label="Activity end time"
              type="time"
              value={form.end_time}
              error={errors.end_time}
              onChange={(event) => set('end_time')(event.target.value)}
            />
          </div>

          <div className={styles.row}>
            <DurationInput
              label="How long is a volunteer normally here?"
              value={defaultMinutes}
              onChange={setDefaultMinutes}
            />
          </div>

          <VolunteerLimits
            sessions={sessions.data ?? []}
            loading={sessions.isPending}
            valueFor={limitFor}
            errors={errors}
            onChange={(sessionId, value) => {
              setLimits((current) => ({ ...current, [sessionId]: value }));
              setErrors((current) => ({ ...current, [`limit:${sessionId}`]: '' }));
            }}
          />

          <div className={styles.row}>
            <TextInput
              label="Activity location"
              value={form.location}
              onChange={(event) => set('location')(event.target.value)}
            />
            <SelectInput
              label="Status"
              placeholder="Select…"
              // Where this activity can go from where it is (Section 8.43).
              options={[activity.status, ...ACTIVITY_STATUS_TRANSITIONS[activity.status]].map(
                (status) => ({
                  value: status,
                  label: humaniseCode(status),
                })
              )}
              value={form.status}
              onChange={(event) => set('status')(event.target.value)}
            />
          </div>

          <TextArea
            label="Activity description"
            rows={3}
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
          />
        </fieldset>

        <div className={styles.actions}>
          <Button type="submit" variant="primary" loading={mutation.isPending}>
            Save changes
          </Button>
          <Button onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * How many volunteers each of this activity's sessions needs.
 *
 * The cap is a property of the SESSION (migration 043), because one activity can
 * carry several and each needs its own number. Almost all carry exactly one -
 * the default session created with the activity - so the common case renders as
 * a single box labelled for the activity, and only an activity that genuinely
 * has several shows several.
 *
 * Blank means no limit, which is not the same as zero: zero would be a session
 * nobody can be assigned to, and the API refuses it.
 *
 * A limit below the number already assigned is accepted on purpose. Those
 * volunteers hold their places and the roster is a fact; what the cap decides is
 * who can still join. See `sessions.service.setCapacity`.
 */
function VolunteerLimits({
  sessions,
  loading,
  valueFor,
  errors,
  onChange,
}: {
  sessions: Session[];
  loading: boolean;
  valueFor: (sessionId: string, stored: number | null) => string;
  errors: Record<string, string>;
  onChange: (sessionId: string, value: string) => void;
}) {
  if (loading) return <LoadingState label="Loading sessions…" />;
  if (sessions.length === 0) return null;

  const single = sessions.length === 1;

  return (
    <>
      {sessions.map((session) => (
        <div className={styles.row} key={session.session_id}>
          <TextInput
            label={single ? 'Volunteers needed' : `Volunteers needed — ${session.session_name}`}
            type="number"
            min={1}
            placeholder="Leave blank for no limit"
            value={valueFor(session.session_id, session.volunteers_needed)}
            error={errors[`limit:${session.session_id}`]}
            onChange={(event) => onChange(session.session_id, event.target.value)}
          />
        </div>
      ))}
    </>
  );
}
